import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  motion,
  useScroll,
  useSpring,
  useInView,
  MotionConfig,
  useReducedMotion,
} from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { MarketingFooter } from "@/components/layout/MarketingFooter";
import { useBilling, Plan as BillingPlan } from "@/hooks/useBilling";
import { useAppAuth } from "@/auth/use-app-auth";
import { useAuthFlow, getOrgFromLocation } from "@/lib/auth-flow";
import { useFeatureMatrix } from "@/hooks/useFeatureMatrix";

// Sections
import { HeroSection } from "./sections/hero-section";
import { EvidenceJourney } from "./sections/evidence-journey";
import "./recruit-home.css";
import { CrisisSection } from "./sections/crisis-section";
import { SearchSection } from "./sections/search-section";
import { InterviewsSection } from "./sections/interviews-section";
import { CitationAISection } from "./sections/citation-ai-section";
import { SocialProofSection } from "./sections/social-proof-section";

// ============================================================================
// SHARED HELPERS (used by PricingSection + FAQ below)
// ============================================================================

const FloatingCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  delay?: number;
}> = ({ children, className, delay = 0 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const reducedMotion = useReducedMotion();

  return (
    <motion.div
      ref={ref}
      initial={reducedMotion ? false : { opacity: 0, y: 20 }}
      animate={isInView || reducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ============================================================================
// PRICING SECTION
// ============================================================================

const PricingSection: React.FC = () => {
  const navigate = useNavigate();
  const { plans, loading, error } = useBilling();
  const { matrix, loading: matrixLoading } = useFeatureMatrix();
  const { isAuthenticated } = useAppAuth();
  const startAuth = useAuthFlow();
  const orgAuthId = getOrgFromLocation();

  const handlePlanClick = async (plan: BillingPlan) => {
    const nameKey = (plan.displayName || plan.name || "").toUpperCase();

    if (!isAuthenticated) {
      await startAuth({ mode: "login", returnTo: "/pricing", orgAuthId });
      return;
    }

    if (nameKey === "FREE") {
      navigate("/dashboard");
      return;
    }

    if (nameKey === "ENTERPRISE") {
      window.open("mailto:apex25@theresume.io?subject=Enterprise Plan Inquiry", "_blank");
      return;
    }

    navigate("/pricing");
  };

  const getButtonText = (plan: BillingPlan) => {
    const nameKey = (plan.displayName || plan.name || "").toUpperCase();
    if (nameKey === "FREE") return "Start Free";
    if (nameKey === "ENTERPRISE") return "Contact Sales";
    return "View plan";
  };

  if (loading || matrixLoading) {
    return (
      <section id="pricing" className="py-32" style={{ background: "#faf9f4" }}>
        <div className="container mx-auto px-6 lg:px-8 text-center">
          <div
            className="animate-spin rounded-full h-8 w-8 border-b mx-auto"
            style={{ borderColor: "#7b5804" }}
          />
          <p className="mt-4 text-sm uppercase tracking-widest" style={{ color: "#4e4637" }}>
            Loading pricing...
          </p>
        </div>
      </section>
    );
  }

  if (error || !Array.isArray(plans)) {
    return (
      <section id="pricing" className="py-32" style={{ background: "#faf9f4" }}>
        <div className="container mx-auto px-6 lg:px-8 text-center">
          <h2>Find the right plan for your team.</h2>
          <p className="mt-4 text-sm">
            Current pricing is temporarily unavailable. Contact us for plan details.
          </p>
          <a
            href="mailto:apex25@theresume.io"
            className="recruit-button recruit-button-secondary mt-6"
          >
            Ask about plans
          </a>
        </div>
      </section>
    );
  }

  return (
    <section
      id="pricing"
      className="py-32 relative overflow-hidden"
      style={{ background: "#faf9f4" }}
    >
      {/* Gold ambient */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[120px] pointer-events-none opacity-30"
        style={{ background: "#d4a853" }}
      />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-20">
            <FloatingCard>
              <span
                className="block text-[10px] uppercase tracking-[0.25em] font-bold mb-6"
                style={{ color: "#7b5804" }}
              >
                Pricing
              </span>
            </FloatingCard>
            <FloatingCard delay={0.1}>
              <h2
                className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight mb-4"
                style={{ color: "#1b1c19" }}
              >
                Transparent.{" "}
                <span className="italic" style={{ color: "#7b5804" }}>
                  Simple. Fair.
                </span>
              </h2>
            </FloatingCard>
            <FloatingCard delay={0.2}>
              <p
                className="text-base max-w-md mx-auto"
                style={{ color: "#4e4637", fontWeight: 300 }}
              >
                Compare the current plans, included features, and usage limits.
              </p>
            </FloatingCard>
          </div>

          {/* Pricing Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {plans
              .filter(Boolean)
              .sort((a, b) => {
                const order = { FREE: 1, STARTER: 2, PROFESSIONAL: 3, BUSINESS: 4, ENTERPRISE: 5 };
                const aKey = (a.displayName || a.name || "").toUpperCase();
                const bKey = (b.displayName || b.name || "").toUpperCase();
                return (
                  (order[aKey as keyof typeof order] || 99) -
                  (order[bKey as keyof typeof order] || 99)
                );
              })
              .map((plan, i) => {
                if (!plan) return null;

                const nameKey = (plan.displayName || plan.name || "").toUpperCase();
                const isPopular = nameKey === "PROFESSIONAL";

                const tierKey =
                  nameKey === "FREE"
                    ? "FREE"
                    : nameKey === "STARTER"
                    ? "STARTER"
                    : nameKey === "PROFESSIONAL"
                    ? "PROFESSIONAL"
                    : nameKey === "BUSINESS"
                    ? "BUSINESS"
                    : "ENTERPRISE";

                const getVal = (key: string) => {
                  const f = matrix?.features.find((x) => x.key === key);
                  if (!f) return { v: undefined as unknown as number | null, p: true };
                  return { v: f.tiers[tierKey] as number | null, p: f.isPackable as boolean };
                };

                const fmt = (key: string, label: string) => {
                  const { v, p } = getVal(key);
                  if (v === undefined) return null;
                  if (v === null) return `${p ? "Unlimited" : "Included"} ${label}`;
                  if (v === 0) return null;
                  return `${v} ${label}`;
                };

                const features =
                  matrix && !matrixLoading
                    ? ([
                        fmt("active_jobs", "job posts"),
                        fmt("byor_uploads", "resumes processed"),
                        fmt("ai_interviews", "AI interviews"),
                        fmt("intro_calls", "intro calls"),
                      ].filter(Boolean) as string[])
                    : ([
                        `${plan.jobPostLimit ?? "Unlimited"} job posts`,
                        `${plan.resumeLimit} resumes processed`,
                        `${plan.interviewLimit} AI interviews`,
                      ].filter(Boolean) as string[]);

                return (
                  <FloatingCard key={plan.id || `${nameKey}-${i}`} delay={i * 0.08}>
                    <div
                      className={cn(
                        "marketing-pricing-card relative h-full transition-all duration-300",
                        isPopular && "marketing-pricing-card--popular"
                      )}
                      style={
                        isPopular
                          ? {
                              background: "#1b1c19",
                              boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
                              transform: "scale(1.02)",
                              zIndex: 10,
                            }
                          : {
                              background: "#ffffff",
                              border: "1px solid rgba(27,28,25,0.08)",
                              boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
                            }
                      }
                    >
                      {/* Top accent bar */}
                      {isPopular && (
                        <div
                          className="h-[3px] w-full"
                          style={{
                            background: "linear-gradient(90deg, #7b5804, #d4a853, #7b5804)",
                          }}
                        />
                      )}

                      {isPopular && (
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                          <span
                            className="px-3 py-1 text-[9px] font-bold uppercase tracking-widest"
                            style={{ background: "#d4a853", color: "#1b1c19" }}
                          >
                            Professional
                          </span>
                        </div>
                      )}

                      <div className="p-7">
                        <h3
                          className="text-[10px] uppercase tracking-widest font-bold mb-5"
                          style={{ color: isPopular ? "rgba(250,249,244,0.40)" : "#807666" }}
                        >
                          {plan.displayName}
                        </h3>

                        {nameKey === "ENTERPRISE" ? (
                          <div
                            className="font-display text-2xl mb-6"
                            style={{ color: isPopular ? "#faf9f4" : "#1b1c19" }}
                          >
                            Contact Us
                          </div>
                        ) : (
                          <div className="flex items-baseline gap-1 mb-6">
                            <span
                              className="font-display text-4xl"
                              style={{ color: isPopular ? "#d4a853" : "#1b1c19" }}
                            >
                              {plan.price}
                            </span>
                            <span
                              className="text-xs"
                              style={{ color: isPopular ? "rgba(250,249,244,0.30)" : "#807666" }}
                            >
                              /mo
                            </span>
                          </div>
                        )}

                        <ul className="space-y-3 mb-8">
                          {features.slice(0, 5).map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2.5 text-xs">
                              <Check
                                className="w-3.5 h-3.5 flex-shrink-0"
                                style={{ color: isPopular ? "#d4a853" : "#7b5804" }}
                              />
                              <span
                                style={{ color: isPopular ? "rgba(250,249,244,0.55)" : "#4e4637" }}
                              >
                                {feature}
                              </span>
                            </li>
                          ))}
                        </ul>

                        <button
                          onClick={() => handlePlanClick(plan)}
                          className="w-full py-3 text-xs font-bold uppercase tracking-widest transition-opacity hover:opacity-80"
                          style={
                            isPopular
                              ? { background: "#d4a853", color: "#1b1c19" }
                              : {
                                  background: "rgba(27,28,25,0.06)",
                                  color: "#1b1c19",
                                  border: "1px solid rgba(27,28,25,0.10)",
                                }
                          }
                        >
                          {getButtonText(plan)}
                        </button>
                      </div>
                    </div>
                  </FloatingCard>
                );
              })}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// FAQ SECTION
// ============================================================================

type FAQCategory = "All" | "Pricing" | "Setup" | "Security" | "Workflow";

const FAQ_ITEMS: Array<{
  category: Exclude<FAQCategory, "All">;
  question: string;
  answer: string;
}> = [
  {
    category: "Workflow",
    question: "What do rankings and citations tell me?",
    answer:
      "Resume discovery surfaces relevant experience; criteria ranking evaluates resumes against your requirements. Citation AI is a separate, job-scoped review tool that links answers to available source passages. These are aids to review, not validated accuracy guarantees or independent checks of candidate claims.",
  },
  {
    category: "Workflow",
    question: "Do I need to change my workflow?",
    answer:
      "Start with a role, discover or upload candidates, review their profiles, and invite selected candidates to AI interviews. Your team reviews the results and decides who moves forward.",
  },
  {
    category: "Workflow",
    question: "What if the AI misses a good candidate?",
    answer:
      "Review beyond the top results, adjust your search and criteria, and follow up on missing information. AI can miss relevant experience; a low ranking should not replace your judgment.",
  },
  {
    category: "Setup",
    question: "What do I need to get started?",
    answer:
      "Create your workspace and a role, then search for candidates or upload resumes. Add hiring criteria and interview questions for the role. Processing time depends on your files and the work requested.",
  },
  {
    category: "Pricing",
    question: "Can I cancel anytime?",
    answer:
      "Subscription cancellation is available through billing. A scheduled cancellation takes effect at the end of the billing period. Check your plan and billing terms before making changes.",
  },
  {
    category: "Setup",
    question: "What file formats do you support?",
    answer:
      "PDF, DOCX, DOC, and TXT are supported. Upload limits depend on the workflow and your plan; follow the file-count and size limits shown in the uploader.",
  },
  {
    category: "Security",
    question: "Who can use job-scoped candidate chat?",
    answer:
      "Job-scoped chat requires an authenticated recruiter with access to the job. Candidate practice interviews are excluded from its retrieval. Contact our team to discuss your organisation’s security and data-handling requirements.",
  },
  {
    category: "Pricing",
    question: "Where can I check features and limits?",
    answer:
      "See the current pricing and feature comparison for the features, credits, and limits included in each plan. Availability may differ between plans and workspaces.",
  },
];

const FAQSection: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<FAQCategory>("All");

  const counts = useMemo(() => {
    const base: Record<FAQCategory, number> = {
      All: FAQ_ITEMS.length,
      Pricing: 0,
      Setup: 0,
      Security: 0,
      Workflow: 0,
    };
    FAQ_ITEMS.forEach((item) => {
      base[item.category] += 1;
    });
    return base;
  }, []);

  const filtered = useMemo(
    () =>
      activeCategory === "All"
        ? FAQ_ITEMS
        : FAQ_ITEMS.filter((item) => item.category === activeCategory),
    [activeCategory]
  );

  const categories: FAQCategory[] = ["All", "Pricing", "Setup", "Security", "Workflow"];

  return (
    <section
      id="faq"
      className="py-32 relative overflow-hidden"
      style={{ background: "#faf9f4", borderTop: "1px solid rgba(27,28,25,0.06)" }}
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <FloatingCard>
              <span
                className="block text-[10px] uppercase tracking-[0.25em] font-bold mb-6"
                style={{ color: "#7b5804" }}
              >
                FAQ
              </span>
            </FloatingCard>
            <FloatingCard delay={0.1}>
              <h2
                className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight"
                style={{ color: "#1b1c19" }}
              >
                Common{" "}
                <span className="italic" style={{ color: "#7b5804" }}>
                  Questions
                </span>
              </h2>
            </FloatingCard>
          </div>

          {/* Category filter pills */}
          <FloatingCard delay={0.15}>
            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {categories.map((cat) => {
                const isActive = activeCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.22em] transition-all"
                    style={
                      isActive
                        ? { background: "#1b1c19", color: "#faf9f4", border: "1px solid #1b1c19" }
                        : {
                            background: "transparent",
                            color: "#4e4637",
                            border: "1px solid rgba(27,28,25,0.12)",
                          }
                    }
                    aria-pressed={isActive}
                  >
                    <span>{cat}</span>
                    <span
                      className="text-[9px]"
                      style={{
                        color: isActive ? "rgba(250,249,244,0.50)" : "#9a907f",
                      }}
                    >
                      {String(counts[cat]).padStart(2, "0")}
                    </span>
                  </button>
                );
              })}
            </div>
          </FloatingCard>

          {/* FAQ Items */}
          <div className="space-y-4">
            {filtered.map((faq, i) => (
              <FloatingCard key={`${faq.category}-${faq.question}`} delay={i * 0.04}>
                <div
                  className="marketing-faq-card p-7 transition-all duration-300"
                  style={{
                    background: "#ffffff",
                    border: "1px solid rgba(27,28,25,0.07)",
                  }}
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <h3 className="font-display text-lg" style={{ color: "#1b1c19" }}>
                      {faq.question}
                    </h3>
                    <span
                      className="shrink-0 mt-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.22em]"
                      style={{
                        background: "rgba(212,168,83,0.12)",
                        color: "#7b5804",
                      }}
                    >
                      {faq.category}
                    </span>
                  </div>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: "#4e4637", fontWeight: 300 }}
                  >
                    {faq.answer}
                  </p>
                </div>
              </FloatingCard>
            ))}
          </div>

          {/* Contact CTA */}
          <FloatingCard delay={0.4}>
            <div
              className="text-center mt-12 p-8"
              style={{ background: "#ffffff", border: "1px solid #d4a853" }}
            >
              <p className="text-sm mb-4" style={{ color: "#4e4637" }}>
                Still have questions?
              </p>
              <a
                href="mailto:apex25@theresume.io"
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest transition-opacity hover:opacity-75"
                style={{ color: "#7b5804" }}
              >
                Contact Support
                <ArrowRight size={14} />
              </a>
            </div>
          </FloatingCard>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const MarketingHomePage: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  return (
    <MotionConfig reducedMotion="user">
      <div
        ref={containerRef}
        className="recruit-home relative min-h-screen antialiased"
        style={{ fontFamily: "'General Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}
      >
        {/* Scroll progress bar */}
        <motion.div
          className="fixed top-0 left-0 right-0 h-px origin-left z-50"
          style={{ scaleX: smoothProgress, background: "#7b5804" }}
        />

        <HeroSection />
        <CrisisSection />
        <SearchSection />
        {/* The same fictional candidate accumulates context through four native-scroll chapters. */}
        <EvidenceJourney />
        <InterviewsSection />
        <CitationAISection />
        <SocialProofSection />
        <PricingSection />
        <FAQSection />

        <section className="recruit-final-cta">
          <div className="recruit-shell">
            <p className="recruit-eyebrow">From application to conversation</p>
            <h2>
              See more than a resume.
              <br />
              <span>Bring a clearer shortlist.</span>
            </h2>
            <p>
              Discover relevant experience, hear candidates in their own words, and make room for
              the questions that matter.
            </p>
            <div className="recruit-actions">
              <button
                className="recruit-button recruit-button-primary"
                onClick={() => {
                  track("CTA - Final - Start Free", {
                    origin: "final-cta",
                    label: "Explore plans",
                  });
                  window.location.href = "/pricing";
                }}
              >
                Explore plans <ArrowRight size={17} />
              </button>
              <a
                className="recruit-button recruit-button-secondary"
                href="mailto:apex25@theresume.io?subject=Demo Request"
              >
                Talk to our team
              </a>
            </div>
          </div>
        </section>
        <MarketingFooter />
      </div>
    </MotionConfig>
  );
};
